import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useCreateFollowUp } from '@/hooks/useStakeholderInteractions';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface FollowUpDialogProps {
  isOpen: boolean;
  onClose: () => void;
  stakeholderId: string;
  stakeholderName: string;
  currentFollowUp?: string;
}

const followUpSchema = z.object({
  date: z.string().min(1, 'Date is required'),
  subject: z.string().min(1, 'Subject is required'),
  notes: z.string().optional(),
});

type FollowUpFormData = z.infer<typeof followUpSchema>;

export function FollowUpDialog({ 
  isOpen, 
  onClose, 
  stakeholderId, 
  stakeholderName,
  currentFollowUp 
}: FollowUpDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createFollowUp = useCreateFollowUp();

  const form = useForm<FollowUpFormData>({
    resolver: zodResolver(followUpSchema),
    defaultValues: {
      date: currentFollowUp ? format(new Date(currentFollowUp), 'yyyy-MM-dd') : '',
      subject: '',
      notes: '',
    },
  });

  const onSubmit = async (data: FollowUpFormData) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await createFollowUp.mutateAsync({
        stakeholderId,
        date: data.date,
        subject: data.subject,
        type: 'Follow-up',
        notes: data.notes,
      });

      toast.success('Follow-up created successfully');
      form.reset();
      onClose();
    } catch (error) {
      console.error('Error creating follow-up:', error);
      toast.error('Failed to create follow-up');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Thêm Follow-up - {stakeholderName}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Follow-up Date *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter subject..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter notes about this follow-up..."
                      className="min-h-[80px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
